import type { BookingLocationType, UserGroupDataQualityPersonalSubject } from '@navio/nsdk';
import { noddiAsync, URLKeys } from 'noddi-async';

type Props = {
  /** Brand rollout switch (customer and partner). Only admin omits it, so bad data reports even when the prompt is off. */
  brandDomain?: string;
  bookingLocationType?: BookingLocationType;
  /** Whose personal details (name, email, phone) NAME/EMAIL/PHONE issues are about; defaults to the group's primary contact. */
  personalSubject?: UserGroupDataQualityPersonalSubject;
  /** Adds per-member personal-detail issues; the backend only serves this to group admins. */
  includeMembers?: boolean;
  /** Null when the surface has no group yet; the query stays idle instead of being keyed on a placeholder id. */
  userGroupId: number | null;
  /** Further reasons to hold the query back (signed out, prompt opted out). Defaults to on. */
  enabled?: boolean;
};

/**
 * Single-source query for ONE group's data-quality report (banner and fix
 * dialog). The endpoint accepts a batch, but every surface passes exactly
 * one group, so this hook is one-group by contract and returns its record.
 */
export const useUserGroupDataQuality = ({
  brandDomain,
  bookingLocationType,
  personalSubject,
  includeMembers,
  userGroupId,
  enabled = true
}: Props) => {
  const query = noddiAsync.useGet({
    type: URLKeys.getUserGroupDataQuality,
    input: {
      userGroupIds: userGroupId != null ? [userGroupId] : [],
      brandDomain,
      bookingLocationType,
      personalSubject,
      includeMembers
    },
    queryConfig: { enabled: enabled && userGroupId != null }
  });
  return { ...query, record: query.data?.[0] };
};
